import { type FormEvent, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";
import prismLogo from "@/assets/prism-logo.png";
import { ApiError } from "@/lib/api";
import { useTotpFirstTimeSetup, useTotpFirstTimeVerify } from "@/lib/auth";
import { AuthChrome } from "@/components/auth/AuthChrome";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { OTPInput } from "@/components/ui/otp-input";
import { Spinner } from "@/components/ui/spinner";

interface Props {
  onSuccess: () => void;
}

/** Step-dot indicator reused inside the card. */
function StepDots({ step }: { step: 1 | 2 | 3 }) {
  const { t } = useTranslation();
  const steps = [
    t("auth.setup2fa_step1"),
    t("auth.setup2fa_step2"),
    t("auth.setup2fa_step3"),
  ] as const;

  return (
    <ol className="flex items-center gap-0" aria-label={t("auth.step_progress")}>
      {steps.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <li key={n} className="flex flex-1 items-center">
            <div className="flex flex-col items-center">
              <span
                className={
                  done
                    ? "flex h-6 w-6 items-center justify-center rounded-full bg-success text-xs font-semibold text-fg-primary"
                    : active
                      ? "flex h-6 w-6 items-center justify-center rounded-full bg-accent text-xs font-semibold text-fg-on-accent"
                      : "flex h-6 w-6 items-center justify-center rounded-full border border-border text-xs font-semibold text-fg-muted"
                }
                aria-current={active ? "step" : undefined}
              >
                {n}
              </span>
              <span className="mt-1 hidden text-center text-xs leading-tight text-fg-muted sm:block">
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={
                  done
                    ? "mx-1 h-px flex-1 bg-success"
                    : "mx-1 h-px flex-1 bg-border"
                }
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function Setup2faPage({ onSuccess }: Props) {
  const { t } = useTranslation();
  const setup = useTotpFirstTimeSetup();
  const verify = useTotpFirstTimeVerify();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [secretRevealed, setSecretRevealed] = useState(false);

  // Auto-fetch the secret on mount. Pre-auth cookie was set during /login.
  useEffect(() => {
    setup.mutate(undefined, {
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.detail);
        } else {
          setError(t("setup2fa.error_setup_failed"));
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (code.length !== 6) {
      setError(t("setup2fa.code_length"));
      return;
    }
    try {
      await verify.mutateAsync({ code });
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.detail);
      } else {
        setError(t("setup2fa.error_verify_failed"));
      }
    }
  };

  /* Loading state */
  if (setup.isPending) {
    return (
      <AuthChrome>
        <div className="flex flex-col items-center gap-3 text-fg-muted">
          <Spinner size="md" />
          <p className="text-sm">{t("setup2fa.loading")}</p>
        </div>
      </AuthChrome>
    );
  }

  /* Setup error state */
  if (setup.isError || !setup.data) {
    return (
      <AuthChrome>
        <Card className="w-full max-w-sm shadow-md">
          <CardContent className="space-y-4 pt-6">
            <Alert tone="danger" title={t("setup2fa.error_setup_failed")}>
              {t("setup2fa.error_setup_hint")}
            </Alert>
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => window.location.assign("/")}
            >
              {t("setup2fa.back_to_login")}
            </Button>
          </CardContent>
        </Card>
      </AuthChrome>
    );
  }

  const { otpauth_uri, secret } = setup.data;

  // Step indicator: 1=install app (before this page), 2=scan QR, 3=enter code.
  // The user lands here already past step 1; advance to step 3 once they type.
  const visualStep: 1 | 2 | 3 = code.length > 0 ? 3 : 2;

  return (
    <AuthChrome>
      <Card className="w-full max-w-md shadow-md">
        <CardHeader className="pb-4">
          <img
            src={prismLogo}
            alt="Prism"
            className="mb-2 h-10 w-auto select-none"
            draggable={false}
          />
          <div className="flex items-center gap-2">
            <ShieldCheck size={24} strokeWidth={2} className="text-accent" aria-hidden="true" />
            <h1 className="text-2xl font-bold tracking-tight text-fg-primary">
              {t("setup2fa.title")}
            </h1>
          </div>
          <p className="mt-1 text-sm text-fg-muted">{t("setup2fa.subtitle")}</p>

          {/* Step indicator */}
          <div className="mt-4">
            <StepDots step={visualStep} />
          </div>
        </CardHeader>

        <CardContent className="space-y-5 pt-0">
          {/* Instructions list */}
          <ol className="space-y-1.5 text-sm text-fg-secondary">
            <li className="flex gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bg-elevated text-xs font-semibold text-fg-muted">
                1
              </span>
              {t("setup2fa.step_install")}
            </li>
            <li className="flex gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bg-elevated text-xs font-semibold text-fg-muted">
                2
              </span>
              {t("setup2fa.step_scan")}
            </li>
            <li className="flex gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bg-elevated text-xs font-semibold text-fg-muted">
                3
              </span>
              {t("setup2fa.step_enter")}
            </li>
          </ol>

          {/* QR code — always white background for scanner compatibility */}
          <div className="flex justify-center rounded-xl bg-white p-4 shadow-sm">
            <QRCodeSVG
              value={otpauth_uri}
              size={192}
              level="M"
              marginSize={2}
              aria-label={t("setup2fa.qr_alt")}
            />
          </div>

          {/* Manual secret reveal — accessible toggle */}
          <div>
            <button
              type="button"
              onClick={() => setSecretRevealed((v) => !v)}
              aria-expanded={secretRevealed}
              className="flex items-center gap-1.5 text-xs text-fg-muted transition-colors hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              {secretRevealed ? (
                <>
                  <ChevronUp size={14} strokeWidth={2} aria-hidden="true" />
                  {t("setup2fa.hide_secret")}
                </>
              ) : (
                <>
                  <ChevronDown size={14} strokeWidth={2} aria-hidden="true" />
                  {t("setup2fa.show_secret")}
                </>
              )}
            </button>
            {secretRevealed && (
              <code className="mt-2 block rounded-lg border border-border bg-bg-elevated p-3 font-mono text-xs leading-relaxed text-fg-secondary break-all">
                {secret}
              </code>
            )}
          </div>

          {/* Code entry form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium text-fg-primary">
                {t("setup2fa.code_label")}
              </p>
              <OTPInput
                value={code}
                onChange={setCode}
                onComplete={() => {
                  /* allow user to review before submitting; they press the button */
                }}
                disabled={verify.isPending}
                autoFocus
                ariaLabel={t("setup2fa.code_label")}
                className="justify-center"
              />
              <p className="text-xs text-fg-muted">{t("setup2fa.code_hint")}</p>
            </div>

            {error && (
              <Alert tone="danger" icon>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              loading={verify.isPending}
              size="lg"
              disabled={code.length !== 6}
            >
              {t("setup2fa.submit")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthChrome>
  );
}
